import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { FullPageSpinner } from '../../components/FullPageSpinner.js';
import { isSuperadminFromToken } from './jwt-claims.js';
import { useSession } from './SessionProvider.js';

/**
 * Guarda de rotas exclusivas do Superadmin. Só pode ser montada DENTRO de
 * `<RequireAuth>` — confia que já existe sessão. Lê a claim `is_superadmin`
 * do JWT (injetada pelo hook `custom_access_token_hook`, migration 0027).
 *
 * Sem a claim OU com a claim `false`, redireciona para `/dashboard`. Em
 * produção isso é reforçado no banco via RLS + `public.is_superadmin()`;
 * aqui é apenas a experiência de UI (não mostrar link quebrado).
 *
 * Uso:
 * ```tsx
 * <Route element={<RequireAuth />}>
 *   <Route element={<RequireSuperadmin />}>
 *     <Route path="/superadmin/settings/ai" element={<AiSettingsPage />} />
 *   </Route>
 * </Route>
 * ```
 */
export function RequireSuperadmin() {
  const { loading, session } = useSession();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!session) {
    // Não deveria acontecer porque RequireAuth roda antes, mas é defensive.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (!isSuperadminFromToken(session.access_token)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}

/**
 * Hook utilitário para componentes que precisam condicionar UI (esconder
 * link, mostrar badge) baseado no papel de Superadmin. Prefira
 * `<RequireSuperadmin>` para proteção de rota — este hook é só para UI.
 */
export function useIsSuperadmin(): boolean {
  const { session } = useSession();
  return isSuperadminFromToken(session?.access_token);
}
